import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* /smartket/slip?listing=<id> — The "Pull a Slip" flow.
   Marketer reviews the exact terms of the listing (commission %,
   template, attribution window, sales platform), reads the plain-
   English T&Cs, digitally signs by typing their name, and submits.
   Slip becomes pending_founder — founder must counter-sign in
   /smartket/slips to activate. */

const TEMPLATE_LABEL = {
  sprint: "Sprint · 90 days from activation",
  steady: "Steady Climb · 12 mo full + 6 mo half taper",
  partnership: "Partnership · up to 24 mo or $10k per customer",
};

export default function SmartKetSlip() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const listingId = sp.get("listing");
  const [listing, setListing] = useState(null);
  const [terms, setTerms] = useState(null);
  const [showFull, setShowFull] = useState(false);
  const [signature, setSignature] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "SmartKet — Pull a Slip";
  }, []);

  useEffect(() => {
    if (!listingId) return;
    axios.get(`${API}/smartket/listings/search`, { params: {} }).then(r => {
      const found = (r.data?.listings || []).find(l => l.id === listingId);
      setListing(found || null);
    });
    axios.get(`${API}/smartket/terms`).then(r => setTerms(r.data));
  }, [listingId]);

  const isOwnListing = listing && user && listing.founder_id === user._id;

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !agree || !signature.trim() || !terms) return;
    setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/smartket/slips`, {
        listing_id: listingId,
        signature_text: signature.trim(),
        accepted_terms_version: terms.version,
      });
      nav(`/smartket/slips?created=${r.data.id}`);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "couldn't submit the slip");
    }
    setBusy(false);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0d1410] text-white p-8 max-w-lg mx-auto">
        <div className="font-mono text-sm">Sign in first to pull a slip.</div>
        <Link to={`/login?next=/smartket/slip?listing=${listingId}`} className="mt-4 inline-block bg-[#d4a34a] text-black font-mono text-xs tracking-widest px-3 py-2">SIGN IN ▸</Link>
      </div>
    );
  }
  if (!listingId) {
    return <div className="min-h-screen bg-[#0d1410] text-white p-8 font-mono text-sm">Missing listing id. <Link to="/smartket/board" className="text-[#d4a34a] underline">back to the board</Link></div>;
  }

  return (
    <div data-testid="slip-page" className="min-h-screen bg-[#0d1410] text-white">
      <div className="border-b-2 border-[#d4a34a]/40 bg-[#141a15]">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
          <Link to="/smartket/board" className="font-mono text-[10px] tracking-[0.4em] text-white/60 hover:text-[#d4a34a]">
            ← SMARTKET · BOARD
          </Link>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-none tracking-tight">
            PULL A <span className="text-[#d4a34a]">SLIP</span>.
          </h1>
          <p className="mt-3 font-mono text-sm text-white/75 max-w-xl">
            You're about to commit to promoting this product. Read the terms. Sign. Founder counter-signs — the slip goes live.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 space-y-6">
        {!listing && <div className="font-mono text-sm text-white/60">loading listing…</div>}

        {isOwnListing && (
          <div data-testid="slip-own-listing" className="border-2 border-yt-red/50 bg-yt-red/10 p-5">
            <div className="font-mono text-[10px] tracking-widest text-yt-red mb-2">HEADS UP</div>
            <div className="font-mono text-sm text-white/90">You can't pull a slip on your own product. This is your listing — head back to the <Link to="/smartket/board" className="text-[#d4a34a] underline">board</Link> to find someone else's product to market.</div>
          </div>
        )}

        {listing && !isOwnListing && (
          <>
            {/* LISTING SNAPSHOT */}
            <section data-testid="slip-listing-snapshot" className="border-2 border-[#d4a34a]/40 bg-[#141a15] p-5">
              <div className="font-mono text-[10px] tracking-widest text-[#d4a34a] mb-1">THE LISTING</div>
              <div className="font-display text-2xl leading-tight mb-1">{listing.title}</div>
              <div className="font-mono text-[11px] text-white/60 mb-3">🌱 {listing.founder_name}</div>
              <p className="font-mono text-xs text-white/80 leading-relaxed mb-4">{listing.description}</p>
              <a href={listing.url} target="_blank" rel="noreferrer"
                 className="inline-block bg-white/10 border border-white/20 hover:bg-white/20 font-mono text-[11px] tracking-widest px-3 py-1.5">
                VIEW PRODUCT ↗
              </a>
            </section>

            {/* TERMS SNAPSHOT — the DEAL SPECIFICS */}
            <section className="border-2 border-white/15 bg-[#0d1410] p-5" data-testid="slip-terms-snapshot">
              <div className="font-mono text-[10px] tracking-widest text-[#d4a34a] mb-3">THE DEAL · LOCKED FOR THIS SLIP</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm font-mono">
                <div className="bg-black/40 p-3 border border-white/10">
                  <div className="text-[10px] tracking-widest text-white/50">COMMISSION</div>
                  <div className="text-2xl text-[#d4a34a] font-display">{listing.commission_pct}%</div>
                  <div className="text-[11px] text-white/60">on every verified sale</div>
                </div>
                <div className="bg-black/40 p-3 border border-white/10">
                  <div className="text-[10px] tracking-widest text-white/50">TEMPLATE</div>
                  <div className="text-sm text-white/90">{TEMPLATE_LABEL[listing.template || "steady"]}</div>
                </div>
                <div className="bg-black/40 p-3 border border-white/10">
                  <div className="text-[10px] tracking-widest text-white/50">ATTRIBUTION WINDOW</div>
                  <div className="text-sm text-white/90">{listing.attribution_days || 60} days from click</div>
                </div>
                <div className="bg-black/40 p-3 border border-white/10">
                  <div className="text-[10px] tracking-widest text-white/50">SALES PLATFORM</div>
                  <div className="text-sm text-white/90">{listing.sales_platform || "not set"}</div>
                </div>
              </div>
            </section>

            {/* T&Cs — plain english + collapse for full legal */}
            {terms && (
              <section className="border-2 border-white/15 bg-[#0d1410] p-5" data-testid="slip-terms-body">
                <div className="font-mono text-[10px] tracking-widest text-[#d4a34a] mb-3">
                  SLIP AGREEMENT · v{terms.version} · plain english
                </div>
                <pre className="whitespace-pre-wrap font-mono text-xs text-white/85 leading-relaxed">{terms.plain_english}</pre>

                <button type="button" onClick={() => setShowFull(v => !v)}
                        data-testid="show-full-terms"
                        className="mt-4 font-mono text-[11px] tracking-widest text-[#d4a34a] hover:text-white underline">
                  {showFull ? "▲ HIDE FULL LEGAL TERMS" : "▼ SHOW FULL LEGAL TERMS"}
                </button>

                {showFull && (
                  <pre data-testid="slip-terms-full" className="mt-4 whitespace-pre-wrap font-mono text-[11px] text-white/70 leading-relaxed border-t border-white/10 pt-4">
{terms.full_legal}
                  </pre>
                )}
              </section>
            )}

            {/* SIGNATURE */}
            <form onSubmit={submit} className="border-2 border-[#d4a34a] bg-[#1a1410] p-5 space-y-3" data-testid="slip-signature-form">
              <div className="font-mono text-[10px] tracking-widest text-[#d4a34a]">DIGITAL SIGNATURE · MARKETER SIDE</div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)}
                       data-testid="slip-agree" className="mt-1 accent-[#d4a34a]" />
                <span className="font-mono text-xs text-white/85 leading-relaxed">
                  I've read the plain-English summary and the full legal terms above. I understand this is a binding agreement between me and the founder. I confirm my identity below by typing my full name.
                </span>
              </label>
              <input value={signature} onChange={e => setSignature(e.target.value)} required maxLength={120}
                     placeholder="Type your full legal name here"
                     data-testid="slip-signature"
                     className="w-full bg-black/40 border border-white/20 focus:border-[#d4a34a] px-3 py-3 text-white text-lg font-display italic outline-none" />
              {err && <div className="font-mono text-xs text-yt-red">{err}</div>}
              <button type="submit" disabled={busy || !agree || !signature.trim()} data-testid="slip-submit"
                      className="bg-[#d4a34a] text-black font-display text-base tracking-widest px-5 py-3 shadow-brutal hover:bg-white disabled:opacity-40">
                {busy ? "signing…" : "SIGN & SEND TO FOUNDER"}
              </button>
              <div className="font-mono text-[10px] text-white/50">
                The slip becomes active only after the founder counter-signs. You'll be able to track its status in <Link to="/smartket/slips" className="text-[#d4a34a] underline">My Slips</Link>.
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
